import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostListener, OnDestroy, inject } from '@angular/core';
import { Authentification } from '../../Services/authentification';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PLATFORM_ID } from '@angular/core';
import { Subscription } from 'rxjs';
import * as am5 from '@amcharts/amcharts5';
import { Installation as InstallationService, ModeAffichageGraphique, type DefautsPrecharges } from '../../Services/installation';
import { ChartPeriodSelectorComponent, PeriodeChangee } from '../../Composants/chart-period-selector/chart-period-selector';
import { EtatService } from '../../Services/etat';
import { statusLabel, statusClass, formaterDateLongue } from '../../Services/format';
import { ChiffresUtilesInstallation, Installation, Tag } from '../../Services/EntiteProjet';
import { MeteoService, MeteoData } from '../../Services/meteo';
import { ChartPoint, createChart as createProductionInstallationChart } from './installation-chart-puissance';
import { InstallationChartService, ChartLabels } from './installation-chart.service';
import { ChartHostOverlayComponent } from '../../Composants/chart-host-overlay/chart-host-overlay';
import { InstallationEtatPanelComponent } from '../../Composants/installation-etat-panel/installation-etat-panel';
import { InstallationProductionCumuleePanelComponent } from '../../Composants/installation-production-cumulee-panel/installation-production-cumulee-panel';
import { InstallationContactsPanelComponent } from '../../Composants/installation-contacts-panel/installation-contacts-panel';
import { ChartFullscreenModalComponent } from '../../Composants/chart-fullscreen-modal/chart-fullscreen-modal';
import { CarteInstallationsComponent } from '../../Composants/carte-installations/carte-installations';
import { EnergieUnitePipe, PuissanceUnitePipe, DeviseUnitePipe, Co2UnitePipe } from '../../Pipes/unite.pipe';

@Component({
  selector: 'app-installation-page',
  imports: [CommonModule, ChartHostOverlayComponent, InstallationEtatPanelComponent, InstallationProductionCumuleePanelComponent, InstallationContactsPanelComponent, ChartFullscreenModalComponent, ChartPeriodSelectorComponent, CarteInstallationsComponent, EnergieUnitePipe, PuissanceUnitePipe, DeviseUnitePipe, Co2UnitePipe],
  templateUrl: './installation.html',
  styleUrl: './installation.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationPage implements AfterViewInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private installationService = inject(InstallationService);
  private etat = inject(EtatService);
  private chartService = inject(InstallationChartService);
  private cdr = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);
  private meteoService = inject(MeteoService);
  private destroyRef = inject(DestroyRef);
  private auth = inject(Authentification);
  private requeteCourante = 0;
  private requeteGraphiqueCourante = 0;
  private chartRoot?: am5.Root;
  private productionChartHost?: HTMLDivElement;
  private productionChartData: ChartPoint[] = [];
  private productionChartLabels: ChartLabels = {
    production: 'Production',
    ensoleillement: 'Ensoleillement',
  };
  private meteoAbonnement?: Subscription;

  readonly statusLabel = statusLabel;
  readonly statusClass = statusClass;

  installation: Installation | null = null;
  defautsPrecharges: DefautsPrecharges | null = null;
  meteo: MeteoData | null = null;
  chiffresUtiles: ChiffresUtilesInstallation | null = null;
  readonly dateLabel = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
  }).replace(/^\w/, c => c.toUpperCase());
  chargement = true;
  chargementGraphique = true;
  erreurChargement = '';
  menuPuissanceOuvert = false;
  modalPuissanceVisible = false;
  modeAffichage: ModeAffichageGraphique = 'jour';
  dateFin = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })();
  modalRefreshToken = 0;
  private pmCache: unknown = null;

  readonly productionChartRenderer = (host: HTMLDivElement): am5.Root => {
    return createProductionInstallationChart(host, {
      data: this.productionChartData,
      production: {
        libelle: this.productionChartLabels.production,
        unite: this.productionChartLabels.uniteProduction
      },
      ensoleillement: {
        libelle: this.productionChartLabels.ensoleillement,
        unite: this.productionChartLabels.uniteEnsoleillement
      },
      mode: this.modeAffichage,
    });
  };

  @HostListener('document:click')
  fermerMenus() {
    this.menuPuissanceOuvert = false;
    this.cdr.markForCheck();
  }

  toggleMenuPuissance(event: MouseEvent) {
    event.stopPropagation();
    this.menuPuissanceOuvert = !this.menuPuissanceOuvert;
  }

  ouvrirModalPuissance() {
    this.menuPuissanceOuvert = false;
    this.modalPuissanceVisible = true;
    this.cdr.markForCheck();
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId) || !this.productionChartHost) return;
    this.initialiserGraphique();
  }

  constructor() {
    this.route.paramMap
      .pipe(takeUntilDestroyed())
      .subscribe(params => {
        const idInstallation = Number(params.get('id'));
        void this.chargerInstallation(idInstallation);
      });
  }

  ngOnDestroy() {
    ++this.requeteCourante;
    ++this.requeteGraphiqueCourante;
    this.chartRoot?.dispose();
    this.meteoAbonnement?.unsubscribe();
  }

  private chargerMeteo(lat: number, lon: number) {
    this.meteoAbonnement?.unsubscribe();
    this.meteoAbonnement = this.meteoService.getMeteo(lat, lon).subscribe({
      next: (m) => { this.meteo = m; this.cdr.markForCheck(); },
      error: ()  => { this.meteo = null; this.cdr.markForCheck(); },
    });
  }

  onProductionChartReady(host: HTMLDivElement) {
    this.productionChartHost = host;
    if (!isPlatformBrowser(this.platformId)) return;
    this.initialiserGraphique();
  }

  private async chargerInstallation(idInstallation: number) {
    const session = this.auth.sessionVersion;
    this.meteoAbonnement?.unsubscribe();
    this.meteo = null;
    this.pmCache = null;
    this.productionChartData = [];
    this.dateFin = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })();
    this.modeAffichage = 'jour';
    console.log('Chargement installation #' + idInstallation + '...');
    const requeteGraphiqueId = ++this.requeteGraphiqueCourante;
    this.definirChargementGraphique(true, `demarrage chargement installation ${idInstallation}`);
    this.cdr.markForCheck();

    // Incremente l'identifiant de requete pour invalider les reponses async
    // des chargements precedents et ne conserver que l'etat le plus recent.
    const requeteId = ++this.requeteCourante;

    this.planifierMiseAJour(requeteId, () => {
      this.chargement = true;
      this.definirChargementGraphique(true, `reinitialisation installation ${idInstallation}`);
      this.erreurChargement = '';
      this.installation = null;
      this.chiffresUtiles = null;
    });

    if (Number.isNaN(idInstallation) || idInstallation <= 0) {
      this.planifierMiseAJour(requeteId, () => {
        this.erreurChargement = 'Identifiant d\'installation invalide.';
        this.chargement = false;
        this.terminerChargementGraphique(requeteGraphiqueId, `identifiant invalide ${idInstallation}`);
      });
      return;
    }

    try {
      // Demarre le chargement du detail de l'installation.
      const installationPromise = this.installationService.getInstallation(idInstallation);
      const promesse = this.installationService.getInstallationDefauts(idInstallation);
      // Le panneau traitera l'erreur, même si elle arrive avant sa création.
      void promesse.catch(() => {});
      const defautsPrecharges = { id: idInstallation, session, promesse };

      // Demarre en parallele le chargement des points de mesure utilises par le graphique.
      const pmPromise = this.installationService.getInstallationPm(idInstallation).catch(() => null);

      // Demarre en parallele le chargement des chiffres utiles (puissance, energie, CO2, gains).
      const chiffresUtilesPromise = this.installationService.getChiffresUtiles(idInstallation).catch(() => null);

      // Attend uniquement le detail de l'installation pour pouvoir afficher le panneau d'etat sans bloquer sur le graphique.
      const installation = await installationPromise;
      if (requeteId !== this.requeteCourante || this.destroyRef.destroyed || session !== this.auth.sessionVersion) return;

      if (!installation) {  //  erreur sur la récupération de l'installation 
        this.planifierMiseAJour(requeteId, () => {
          this.erreurChargement = 'Installation introuvable.';
          this.chargement = false;
          this.terminerChargementGraphique(requeteGraphiqueId, `installation ${idInstallation} introuvable`);
        });
        return;
      }

      this.planifierMiseAJour(requeteId, () => {
        this.installation = installation;
        this.defautsPrecharges = defautsPrecharges;
        this.etat.installationCourante.set(installation);
        this.chargement = false;
        if (Number.isFinite(installation.lat) && Number.isFinite(installation.lon)
            && !(installation.lat === 0 && installation.lon === 0)) {
          this.chargerMeteo(installation.lat, installation.lon);
        }
      });

      void pmPromise
        .then((pm) => {
          if (requeteId !== this.requeteCourante || this.destroyRef.destroyed || session !== this.auth.sessionVersion) return;
          this.pmCache = pm;
          return this.generation_graphe_puissance(pm, ++this.requeteGraphiqueCourante);
        })
        .catch((error) => {
          console.error('Impossible de charger les donnees du graphique', error);
          setTimeout(() => {
            this.terminerChargementGraphique(
              requeteGraphiqueId,
              `erreur chargement graphique installation ${idInstallation}`
            );
            this.cdr.markForCheck();
          }, 0);
        });

      void chiffresUtilesPromise
        .then((chiffresUtiles) => {
          if (session !== this.auth.sessionVersion) return;
          this.planifierMiseAJour(requeteId, () => {
            this.chiffresUtiles = chiffresUtiles;
          });
        })
        .catch((error) => {
          console.error('Impossible de charger les chiffres utiles', error);
        });
    } catch (error) {
      console.error('Impossible de charger l\'installation', error);
      this.planifierMiseAJour(requeteId, () => {
        this.erreurChargement = 'Le chargement de l\'installation a échoué.';
        this.chargement = false;
        this.terminerChargementGraphique(requeteGraphiqueId, `erreur chargement installation ${idInstallation}`);
      });
    }
  }

  /**
   * Differe une mise a jour de l'etat du composant et l'ignore si une requete
   * plus recente a ete lancee entre-temps.
   * Permet d'eviter qu'une reponse asynchrone obsolete ecrase l'etat courant.
   * 
   * @param requeteId Identifiant de la requete a laquelle la mise a jour est associee.
   * @param miseAJour Fonction executant la mutation d'etat si la requete est toujours courante.
   */
  private planifierMiseAJour(requeteId: number, miseAJour: () => void) {
    const session = this.auth.sessionVersion;
    setTimeout(() => {
      if (requeteId !== this.requeteCourante || this.destroyRef.destroyed || session !== this.auth.sessionVersion) return;
      miseAJour();
      this.cdr.markForCheck();
    }, 0);
  }

  private definirChargementGraphique(estVisible: boolean, contexte: string) {
    this.chargementGraphique = estVisible;
    console.log(
      estVisible
        ? `[SPINNER] affiché - ${contexte}`
        : `[SPINNER] masqué - ${contexte}`
    );
  }

  private terminerChargementGraphique(requeteGraphiqueId: number, contexte: string) {
    if (requeteGraphiqueId !== this.requeteGraphiqueCourante) {
      console.log(`[SPINNER] fin ignoree - ${contexte}`);
      return;
    }

    this.definirChargementGraphique(false, contexte);
  }

  /** Indique si l'installation possède des informations DEIE. */
  get afficherDeie(): boolean {
    const deie = this.installation?.DEIE;
    return !!deie && !Array.isArray(deie);
  }

  /** Liste des tags de l'installation (champ `lie` === 't'). */
  get tagsAffiches(): Tag[] {
    return (this.installation?.tags ?? []).filter(tag => tag.lie?.toLowerCase() === 't');
  }

  /** Indique si'installation est en autoconsommation. */
  get estAutoconso(): boolean {
    const t = this.installation?.type_install?.trim().toUpperCase();
    return t === 'ACI' || t === 'ACI0';
  }

  /** Adresse postale complète formatée sur une ligne. */
  get adresse(): string {
    if (!this.installation) return '';
    return [
      this.installation.adresse1,
      this.installation.adresse2,
      this.installation.cp,
      this.installation.ville,
    ].filter(Boolean).join(', ');
  }

  /** Tarif formaté en notation française, ou '—' si absent. */
  get tarifFormate(): string {
    const tarif = this.installation?.tarif;
    if (tarif == null) return '—';
    return tarif.toLocaleString('fr-FR');
  }

  /** Date de mise en service formatée en français, ou '—' si absente. */
  get miseEnService(): string {
    const raw = this.installation?.mise_service;
    if (!raw) return '—';
    const [day, month, year] = raw.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return formaterDateLongue(date);
  }

  onPeriodeChange({ mode, dateFin }: PeriodeChangee) {
    this.modeAffichage = mode;
    this.dateFin = dateFin;
    this.rechargerGraphique(`periode ${mode}`);
  }

  private rechargerGraphique(contexte: string) {
    const requeteGraphiqueId = ++this.requeteGraphiqueCourante;
    this.definirChargementGraphique(true, contexte);
    this.cdr.markForCheck();
    void this.generation_graphe_puissance(this.pmCache, requeteGraphiqueId);
  }

  private async generation_graphe_puissance(pm: unknown, requeteGraphiqueId: number) {
    if (requeteGraphiqueId !== this.requeteGraphiqueCourante || this.destroyRef.destroyed) return;
    const idInstallation = Number(this.route.snapshot.paramMap.get('id'));
    try {
      const { data, labels } = await this.chartService.chargerDonnees(idInstallation, pm, this.modeAffichage, this.dateFin);
      if (requeteGraphiqueId !== this.requeteGraphiqueCourante || this.destroyRef.destroyed) return;
      this.productionChartData = data;
      this.productionChartLabels = labels;
      this.initialiserGraphique(requeteGraphiqueId);
    } catch (error) {
      if (requeteGraphiqueId !== this.requeteGraphiqueCourante || this.destroyRef.destroyed) return;
      console.error('Impossible de charger les données du graphique', error);
      this.terminerChargementGraphique(requeteGraphiqueId, 'erreur chargement données graphique');
      this.cdr.markForCheck();
    }
  }

  private initialiserGraphique(requeteGraphiqueId?: number) {
    if (this.destroyRef.destroyed || (requeteGraphiqueId != null && requeteGraphiqueId !== this.requeteGraphiqueCourante)) return;
    const host = this.productionChartHost;
    if (!host) {
      if (requeteGraphiqueId != null) {
        this.terminerChargementGraphique(requeteGraphiqueId, 'conteneur graphique introuvable');
      } else {
        this.definirChargementGraphique(false, 'conteneur graphique introuvable');
      }
      this.cdr.markForCheck();
      return;
    }

    try {
      this.chartRoot?.dispose();
      console.log('[SPINNER] initialisation du graphique');
      this.chartRoot = createProductionInstallationChart(host, {
        data: this.productionChartData,
        production: {
          libelle: this.productionChartLabels.production,
          unite: this.productionChartLabels.uniteProduction
        },
        ensoleillement: {
          libelle: this.productionChartLabels.ensoleillement,
          unite: this.productionChartLabels.uniteEnsoleillement
        },
        mode: this.modeAffichage,
      });
    } catch (error) {
      console.error('Erreur lors de l’initialisation du graphique', error);
    } finally {
      setTimeout(() => {
        if (this.destroyRef.destroyed || (requeteGraphiqueId != null && requeteGraphiqueId !== this.requeteGraphiqueCourante)) return;
        if (requeteGraphiqueId != null) {
          this.terminerChargementGraphique(requeteGraphiqueId, 'graphique initialise');
        } else {
          this.definirChargementGraphique(false, 'graphique initialise');
        }
        if (this.modalPuissanceVisible) {
          this.modalRefreshToken++;
        }
        this.cdr.markForCheck();
      }, 0);
    }
  }

}
